import React, { useState, useEffect } from 'react';
import { Search, X, RefreshCw, Tag, ChevronDown, ChevronUp } from 'lucide-react';
import { useSessions, SessionsFilters, useAdminScope } from '../../lib/api';
import { SessionSummary } from '../../lib/types';
import { useAuth } from '../../contexts/AuthContext';
import Pagination from '../../components/common/Pagination';
import LoadingState from '../../components/common/LoadingState';
import ErrorState from '../../components/common/ErrorState';
import MarkRefundedModal from '../../components/MarkRefundedModal';
import { formatDate, formatDanishNumber, getSessionStatusColor, getPaymentStatusColor } from '../../lib/utils';

const SessionsPage: React.FC = () => {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    // Read-only advisors/investors/interns. The API already strips the plate,
    // session ID and payment ID for this role and refuses plate searches; the
    // page just doesn't draw what will never arrive.
    const isInsights = user?.role === 'insights';
    const { scope } = useAdminScope();

    // Cities identify a session either by areaNo (EasyPark) or by zone (PRS), and
    // whichever one doesn't apply is always blank. When every city this account
    // can see agrees, name the column after it instead of showing a column that
    // is empty by definition. Mixed or unknown → keep both, as before.
    const identifiers = new Set((scope?.cities || []).map(city => city.sessionIdentifier));
    const identifierLabel =
        identifiers.size === 1 && identifiers.has('zone') ? 'Zone'
        : identifiers.size === 1 && identifiers.has('areaNo') ? 'Area No'
        : 'Area No / Zone';

    const [filters, setFilters] = useState<SessionsFilters>({
        page: 1,
        limit: 50,
        sort: 'startTime',
        order: 'desc',
        summary: false,
        includeHidden: false, // hide 0.00 kr and test-plate sessions by default
    });

    const [showSummary, setShowSummary] = useState(false);
    const [sortField, setSortField] = useState<string>('startTime');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [selectedSessions, setSelectedSessions] = useState<Set<string>>(new Set());
    const [expandedAddresses, setExpandedAddresses] = useState<Set<string>>(new Set());
    const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
    const [refundModalSessionIds, setRefundModalSessionIds] = useState<string[]>([]);

    const { sessions, pagination, summary, isLoading, error, mutate } = useSessions(filters);

    // Update filters when sort changes
    useEffect(() => {
        setFilters(prev => ({
            ...prev,
            sort: sortField,
            order: sortOrder,
        }));
    }, [sortField, sortOrder]);

    const handleFilterChange = (key: string, value: string | number | boolean) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: 1, // Reset to first page when filters change
        }));
    };

    const handleClearFilters = () => {
        setFilters({
            page: 1,
            limit: 50,
            sort: 'startTime',
            order: 'desc',
            summary: false,
            includeHidden: false,
        });
        setShowSummary(false);
    };

    const handleSort = (field: string) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('desc');
        }
    };

    const handlePageChange = (newPage: number) => {
        setFilters(prev => ({ ...prev, page: newPage }));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const toggleSummary = () => {
        const newShowSummary = !showSummary;
        setShowSummary(newShowSummary);
        handleFilterChange('summary', newShowSummary);
    };

    const handleSelectSession = (sessionId: string) => {
        setSelectedSessions(prev => {
            const newSet = new Set(prev);
            if (newSet.has(sessionId)) {
                newSet.delete(sessionId);
            } else {
                newSet.add(sessionId);
            }
            return newSet;
        });
    };

    const toggleAddress = (sessionId: string) => {
        setExpandedAddresses(prev => {
            const next = new Set(prev);
            if (next.has(sessionId)) {
                next.delete(sessionId);
            } else {
                next.add(sessionId);
            }
            return next;
        });
    };

    const handleSelectAll = () => {
        if (selectedSessions.size === sessions.length) {
            setSelectedSessions(new Set());
        } else {
            // Only select sessions that are not already refunded
            const eligibleSessions = sessions
                .filter(s => !s.refundStatus)
                .map(s => s.sessionId)
                .filter((id): id is string => !!id);
            setSelectedSessions(new Set(eligibleSessions));
        }
    };

    const handleMarkRefunded = (sessionId?: string) => {
        if (sessionId) {
            setRefundModalSessionIds([sessionId]);
        } else {
            // Bulk action
            setRefundModalSessionIds(Array.from(selectedSessions));
        }
        setIsRefundModalOpen(true);
    };

    const handleRefundSuccess = () => {
        mutate();
        setSelectedSessions(new Set());
    };

    if (isLoading && !sessions.length) {
        return <LoadingState message="Loading sessions..." />;
    }

    if (error) {
        return <ErrorState error={error.message || 'An error occurred'} onRetry={() => mutate()} />;
    }

    const statusOptions = [
        { value: 'all', label: 'All Statuses' },
        { value: 'active', label: 'Active' },
        { value: 'pending', label: 'Pending' },
        { value: 'completed', label: 'Completed' },
        { value: 'completed_with_refund', label: 'Completed with Refund' },
        { value: 'cancelled', label: 'Cancelled' },
        { value: 'failed', label: 'Failed' },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Sessions</h1>
                    <p className="text-gray-600">Parking session management and monitoring</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => mutate()}
                        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                        title="Refresh data"
                    >
                        <RefreshCw className="h-5 w-5" />
                    </button>
                </div>
            </div>


            {/* Filter Controls */}
            <div className="bg-white border rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    {/* Status Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Status
                        </label>
                        <select
                            value={filters.status || 'all'}
                            onChange={(e) => {
                                const value = e.target.value === 'all' ? undefined : e.target.value;
                                handleFilterChange('status', value || '');
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            {statusOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Date Range */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Start Date
                        </label>
                        <input
                            type="datetime-local"
                            value={filters.startDate ? new Date(filters.startDate).toISOString().slice(0, 16) : ''}
                            onChange={(e) => {
                                const value = e.target.value ? new Date(e.target.value).toISOString() : undefined;
                                handleFilterChange('startDate', value || '');
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            End Date
                        </label>
                        <input
                            type="datetime-local"
                            value={filters.endDate ? new Date(filters.endDate).toISOString().slice(0, 16) : ''}
                            onChange={(e) => {
                                const value = e.target.value ? new Date(e.target.value).toISOString() : undefined;
                                handleFilterChange('endDate', value || '');
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>

                    {/* Area number / zone filter (Admin only) */}
                    {isAdmin && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Area No / Zone (comma-separated)
                            </label>
                            <input
                                type="text"
                                placeholder="e.g., 9031, Blå"
                                value={filters.areaNo || ''}
                                onChange={(e) => handleFilterChange('areaNo', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>
                    )}

                    {/* License Plate Search */}
                    {!isInsights && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                License Plate
                            </label>
                            <div className="relative">
                                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search by license plate"
                                    value={filters.licensePlate || ''}
                                    onChange={(e) => handleFilterChange('licensePlate', e.target.value)}
                                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={toggleSummary}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                showSummary
                                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            {showSummary ? 'Hide' : 'Show'} Summary
                        </button>
                        {/* FreeSpot's own convenience filter, so the founder sees the
                            payments they did not make themselves. Municipality accounts
                            always see everything — those are real parkings on their
                            system — which the server enforces regardless of this button. */}
                        {isAdmin && (
                            <button
                                onClick={() => handleFilterChange('includeHidden', !filters.includeHidden)}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                    filters.includeHidden
                                        ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                                title="By default, sessions with 0.00 kr total and the AB12345 test plate are hidden. Click to show them."
                            >
                                {filters.includeHidden ? 'Hide free / test sessions' : 'Show free / test sessions'}
                            </button>
                        )}
                        <button
                            onClick={handleClearFilters}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-300 transition-colors flex items-center gap-2"
                        >
                            <X className="h-4 w-4" />
                            Clear Filters
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-700">Results per page:</label>
                        <select
                            value={filters.limit || 50}
                            onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Summary Section */}
            {showSummary && summary && summary.byAreaNo && (
                <div className="bg-white border rounded-lg p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-1">
                        Summary by {identifierLabel === 'Zone' ? 'zone' : identifierLabel === 'Area No' ? 'area' : 'area / zone'}
                    </h2>
                    <p className="text-sm text-gray-600 mb-4">
                        Covers every session matching the current filters, not just this page.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Object.values(summary.byAreaNo).map((areaSummary: SessionSummary) => (
                            <div key={areaSummary.key || areaSummary.areaNo} className="border rounded-lg p-4 bg-gray-50">
                                <h3 className="font-semibold text-gray-900 mb-2">
                                    {areaSummary.label || `Area ${areaSummary.areaNo}`}
                                </h3>
                                <div className="space-y-1 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Transactions:</span>
                                        <span className="font-medium">{areaSummary.transactionCount}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Total Cost:</span>
                                        <span className="font-medium">{formatDanishNumber(areaSummary.totalParkingCost)} DKK</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Avg Time:</span>
                                        <span className="font-medium">{areaSummary.avgParkingTime.toFixed(1)} min</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Avg Cost:</span>
                                        <span className="font-medium">{formatDanishNumber(areaSummary.avgParkingCost)} DKK</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Rate:</span>
                                        <span className="font-medium">{areaSummary.currentRate}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Bulk Actions (Admin only) */}
            {isAdmin && selectedSessions.size > 0 && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Tag className="h-5 w-5 text-indigo-600" />
                        <span className="text-sm font-medium text-indigo-900">
                            {selectedSessions.size} session(s) selected
                        </span>
                    </div>
                    <button
                        onClick={() => handleMarkRefunded()}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center gap-2"
                    >
                        <Tag className="h-4 w-4" />
                        Mark Selected as Refunded
                    </button>
                </div>
            )}

            {/* Sessions Table */}
            <div className="bg-white border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                {isAdmin && (
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <input
                                            type="checkbox"
                                            checked={selectedSessions.size === sessions.length && sessions.length > 0}
                                            onChange={handleSelectAll}
                                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        />
                                    </th>
                                )}
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Address
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {identifierLabel}
                                </th>
                                {!isInsights && (
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        License Plate
                                    </th>
                                )}
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('startTime')}
                                >
                                    Start Time
                                    {sortField === 'startTime' && (
                                        <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                                    )}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    End Time
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Duration
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Parking Cost
                                </th>
                                <th
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                    onClick={() => handleSort('pricing.total')}
                                >
                                    Total Amount
                                    {sortField === 'pricing.total' && (
                                        <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                                    )}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Payment Status
                                </th>
                                {!isInsights && (
                                    <th
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('sessionId')}
                                    >
                                        Session ID
                                        {sortField === 'sessionId' && (
                                            <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                                        )}
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {sessions.length === 0 ? (
                                <tr>
                                    <td colSpan={isAdmin ? 12 : isInsights ? 9 : 11} className="px-6 py-8 text-center text-gray-500">
                                        No sessions found
                                    </td>
                                </tr>
                            ) : (
                                sessions.map((session, index) => {
                                    // Insights rows carry no session ID; their position on the page is key enough.
                                    const rowKey = session.sessionId ?? `row-${index}`;
                                    const isSelected = selectedSessions.has(rowKey);
                                    const isRefunded = !!session.refundStatus;
                                    const address = session.address?.trim() || null;
                                    const isAddressExpanded = expandedAddresses.has(rowKey);
                                    const isAddressExpandable = !!address && address.length > 24;
                                    return (
                                    <tr key={rowKey} className="hover:bg-gray-50">
                                        {isAdmin && (
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => handleSelectSession(rowKey)}
                                                    disabled={isRefunded}
                                                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                                />
                                            </td>
                                        )}
                                        <td className="px-6 py-4 text-sm text-gray-900">
                                            {address ? (
                                                isAddressExpandable ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleAddress(rowKey)}
                                                        aria-expanded={isAddressExpanded}
                                                        className="flex max-w-xs items-start gap-1.5 text-left hover:text-indigo-600"
                                                        title={isAddressExpanded ? 'Collapse address' : address}
                                                    >
                                                        <span className={isAddressExpanded ? 'break-words' : 'block max-w-[12rem] truncate'}>
                                                            {address}
                                                        </span>
                                                        {isAddressExpanded ? (
                                                            <ChevronUp className="mt-0.5 h-4 w-4 flex-shrink-0" />
                                                        ) : (
                                                            <ChevronDown className="mt-0.5 h-4 w-4 flex-shrink-0" />
                                                        )}
                                                    </button>
                                                ) : (
                                                    address
                                                )
                                            ) : (
                                                <span className="text-gray-400">—</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {session.zone || session.areaNo || <span className="text-gray-400">—</span>}
                                        </td>
                                        {!isInsights && (
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {session.licensePlate}
                                            </td>
                                        )}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSessionStatusColor(session.status)}`}>
                                                {session.status.replace(/_/g, ' ')}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDate(session.startTime)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {session.status === 'active' || session.status === 'pending' ? (
                                                <span>
                                                    {formatDate(session.endTime)}
                                                    {session.calculatedEndTime && (
                                                        <span className="text-xs text-gray-400 ml-1">(calculated)</span>
                                                    )}
                                                </span>
                                            ) : (
                                                formatDate(session.endTime)
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {session.duration} min
                                            {session.status === 'active' || session.status === 'pending' ? (
                                                <span className="text-xs text-gray-400 ml-1">(planned)</span>
                                            ) : null}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {formatDanishNumber(session.pricing.parkingCost)} {session.pricing.currency}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {formatDanishNumber(session.pricing.total)} {session.pricing.currency}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPaymentStatusColor(session.payment.status)}`}>
                                                {session.payment.status}
                                            </span>
                                        </td>
                                        {!isInsights && (
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                                                {session.sessionId?.substring(0, 8)}...
                                            </td>
                                        )}
                                    </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {pagination && (
                <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                        Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                        {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                        {pagination.total.toLocaleString()} results
                    </div>
                    <Pagination 
                        pagination={{
                            page: pagination.page,
                            pages: pagination.totalPages,
                            total: pagination.total,
                            hasNext: pagination.hasNext,
                            hasPrev: pagination.hasPrev,
                        }} 
                        onPageChange={handlePageChange} 
                    />
                </div>
            )}

            {/* Refund Modal — admin-only; it loads the admin-only campaigns list on mount */}
            {isAdmin && (
                <MarkRefundedModal
                    isOpen={isRefundModalOpen}
                    onClose={() => setIsRefundModalOpen(false)}
                    sessionIds={refundModalSessionIds}
                    sessions={sessions}
                    onSuccess={handleRefundSuccess}
                />
            )}
        </div>
    );
};

export default SessionsPage;
